import { useId, type FormEvent } from "react";
import { parseReminderDays } from "@/api/receivables";
import type { CompanySettings, CompanySettingsInput, TaxMode } from "@/api/settings";
import { RequestError } from "@/components/RequestError";
import { SelectField, TextAreaField, TextField } from "@/components/ui/field";
import { FormActions } from "@/components/ui/form-actions";
import { useFormValues, useSubmitState } from "@/lib/use-form";

const TAX_MODES = [
  { value: "export_no_gst", label: "Export - no GST charged (not GST registered)" },
  { value: "export_lut", label: "Export under LUT (GST registered)" },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </legend>
      <div className="grid gap-4 md:grid-cols-2">{children}</div>
    </fieldset>
  );
}

type CompanyFormProps = {
  initial: CompanySettings;
  onSubmit: (input: CompanySettingsInput) => Promise<unknown>;
  onReload?: () => void;
};

export function CompanyForm({ initial, onSubmit, onReload }: CompanyFormProps) {
  const id = useId();
  const { values, set } = useFormValues({
    legal_name: initial.legal_name,
    short_name: initial.short_name,
    address: initial.address,
    cin: initial.cin,
    pan: initial.pan,
    gstin: initial.gstin,
    email: initial.email,
    phone: initial.phone,
    tax_mode: initial.tax_mode,
    sac_code: initial.sac_code,
    signatory_name: initial.signatory_name,
    signatory_title: initial.signatory_title,
    default_payment_days: String(initial.default_payment_days),
    default_currency: initial.default_currency,
    realisation_months: String(initial.realisation_months),
    first_invoice_serial: String(initial.first_invoice_serial),
    gst_threshold_inr: initial.gst_threshold_inr,
    planned_monthly_income_inr: initial.planned_monthly_income_inr ?? "",
    reminder_days: initial.reminder_days.join(", "),
    credit_note_prefix: initial.credit_note_prefix,
    books_locked_through: initial.books_locked_through ?? "",
    income_tax_percent: initial.income_tax_percent,
  });
  const { error, errors, saving, run } = useSubmitState();
  const lut = values.tax_mode === "export_lut";

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const input: CompanySettingsInput = {
      legal_name: values.legal_name.trim(),
      short_name: values.short_name.trim(),
      address: values.address.trim(),
      cin: values.cin.trim(),
      pan: values.pan.trim(),
      gstin: values.gstin.trim(),
      email: values.email.trim(),
      phone: values.phone.trim(),
      tax_mode: values.tax_mode,
      sac_code: values.sac_code.trim(),
      signatory_name: values.signatory_name.trim(),
      signatory_title: values.signatory_title.trim(),
      default_payment_days: Number.parseInt(values.default_payment_days, 10),
      default_currency: values.default_currency.trim(),
      realisation_months: Number.parseInt(values.realisation_months, 10),
      first_invoice_serial: Number.parseInt(values.first_invoice_serial, 10),
      gst_threshold_inr: values.gst_threshold_inr.trim(),
      planned_monthly_income_inr: values.planned_monthly_income_inr.trim() || null,
      reminder_days: parseReminderDays(values.reminder_days),
      credit_note_prefix: values.credit_note_prefix.trim().toUpperCase(),
      books_locked_through: values.books_locked_through || null,
      income_tax_percent: values.income_tax_percent.trim(),
    };
    void run(() => onSubmit(input));
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
      <RequestError error={error} onReload={onReload} />
      <Section title="Legal identity">
        <TextField
          id={`${id}-legal-name`}
          label="Legal name"
          value={values.legal_name}
          onChange={(value) => set("legal_name", value)}
          error={errors.legal_name}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-short-name`}
          label="Short name"
          value={values.short_name}
          onChange={(value) => set("short_name", value)}
          error={errors.short_name}
          hint="Used in the footer and payment block"
          className="md:col-span-2"
        />
        <TextAreaField
          id={`${id}-address`}
          label="Registered office"
          value={values.address}
          onChange={(value) => set("address", value)}
          error={errors.address}
          rows={2}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-cin`}
          label="CIN"
          value={values.cin}
          onChange={(value) => set("cin", value)}
          error={errors.cin}
          autoCapitalize="characters"
        />
        <TextField
          id={`${id}-pan`}
          label="PAN"
          value={values.pan}
          onChange={(value) => set("pan", value)}
          error={errors.pan}
          hint="10 characters; must match the GSTIN"
          autoCapitalize="characters"
          maxLength={10}
        />
        <TextField
          id={`${id}-gstin`}
          label="GSTIN"
          value={values.gstin}
          onChange={(value) => set("gstin", value)}
          error={errors.gstin}
          hint={lut ? "Required when exporting under LUT" : "Leave blank if not GST registered"}
          autoCapitalize="characters"
          maxLength={15}
        />
      </Section>

      <Section title="Contact">
        <TextField
          id={`${id}-email`}
          label="E-mail"
          type="email"
          value={values.email}
          onChange={(value) => set("email", value)}
          error={errors.email}
        />
        <TextField
          id={`${id}-phone`}
          label="Phone"
          type="tel"
          value={values.phone}
          onChange={(value) => set("phone", value)}
          error={errors.phone}
        />
      </Section>

      <Section title="Invoicing">
        <SelectField
          id={`${id}-tax-mode`}
          label="Tax mode"
          value={values.tax_mode}
          onChange={(value) => set("tax_mode", value as TaxMode)}
          options={TAX_MODES}
          error={errors.tax_mode}
          hint={lut ? "Invoices are titled TAX INVOICE and carry the LUT wording." : "Invoices are titled INVOICE."}
          className="md:col-span-2"
        />
        <TextField
          id={`${id}-sac`}
          label="SAC code"
          inputMode="numeric"
          value={values.sac_code}
          onChange={(value) => set("sac_code", value)}
          error={errors.sac_code}
          hint="Confirm with your CA"
        />
        <TextField
          id={`${id}-currency`}
          label="Default currency"
          value={values.default_currency}
          onChange={(value) => set("default_currency", value)}
          error={errors.default_currency}
          maxLength={3}
        />
        <TextField
          id={`${id}-payment-days`}
          label="Default payment days"
          type="number"
          inputMode="numeric"
          value={values.default_payment_days}
          onChange={(value) => set("default_payment_days", value)}
          error={errors.default_payment_days}
        />
        <TextField
          id={`${id}-realisation`}
          label="Realisation period (months)"
          type="number"
          inputMode="numeric"
          value={values.realisation_months}
          onChange={(value) => set("realisation_months", value)}
          error={errors.realisation_months}
          hint="FEMA limit for receiving export payments - confirm the current RBI period with your CA"
        />
        <TextField
          id={`${id}-first-serial`}
          label="First invoice number of a new year"
          type="number"
          inputMode="numeric"
          value={values.first_invoice_serial}
          onChange={(value) => set("first_invoice_serial", value)}
          error={errors.first_invoice_serial}
          hint="101 gives PBO/26-27/0101. Only years with no invoices yet use a change."
        />
        <TextField
          id={`${id}-credit-prefix`}
          label="Credit note prefix"
          value={values.credit_note_prefix}
          onChange={(value) => set("credit_note_prefix", value.toUpperCase())}
          error={errors.credit_note_prefix}
          maxLength={5}
          autoCapitalize="characters"
          hint="CN gives CN/26-27/0001 - confirm it with the CA before the first credit note"
        />
        <TextField
          id={`${id}-books-lock`}
          label="Books locked through"
          type="date"
          value={values.books_locked_through}
          onChange={(value) => set("books_locked_through", value)}
          error={errors.books_locked_through}
          hint="Nothing may be dated on or before this day; leave empty for no lock"
        />
        <TextField
          id={`${id}-tax-percent`}
          label="Income tax rate (%)"
          inputMode="decimal"
          value={values.income_tax_percent}
          onChange={(value) => set("income_tax_percent", value)}
          error={errors.income_tax_percent}
          hint="25.17 is section 115BAA with surcharge and cess; the P&L shows an estimate at this rate"
        />
        <TextField
          id={`${id}-gst-threshold`}
          label="GST registration threshold (INR)"
          inputMode="decimal"
          value={values.gst_threshold_inr}
          onChange={(value) => set("gst_threshold_inr", value)}
          error={errors.gst_threshold_inr}
          hint="Rs 20,00,000 for services. The reports warn at 75% and 90% of it."
        />
        <TextField
          id={`${id}-planned-income`}
          label="Planned monthly income (INR)"
          inputMode="decimal"
          value={values.planned_monthly_income_inr}
          onChange={(value) => set("planned_monthly_income_inr", value)}
          error={errors.planned_monthly_income_inr}
          hint="Drives each bucket's budget; leave empty for no budgets"
        />
        <TextField
          id={`${id}-reminder-days`}
          label="Payment reminders (days after the due date)"
          value={values.reminder_days}
          onChange={(value) => set("reminder_days", value)}
          error={errors.reminder_days}
          hint="1, 8, 15 sends three reminders; leave empty to stop the daily reminders"
        />
      </Section>

      <Section title="Signatory">
        <TextField
          id={`${id}-signatory`}
          label="Signatory name"
          value={values.signatory_name}
          onChange={(value) => set("signatory_name", value)}
          error={errors.signatory_name}
        />
        <TextField
          id={`${id}-signatory-title`}
          label="Signatory title"
          value={values.signatory_title}
          onChange={(value) => set("signatory_title", value)}
          error={errors.signatory_title}
        />
      </Section>

      <FormActions saving={saving} submitLabel="Save company details" />
    </form>
  );
}
